/* eslint react/no-did-mount-set-state: 0 */

import React, {useState, useRef, useEffect} from 'react';
import PropTypes from 'prop-types';
import EdapViewTracker from '../edap-view-tracker/EdapViewTracker';

import {pushEdapEvent} from '../../../utils/edap';

import {CHEVRON_DOWN__16} from '@homeaway/svg-defs';
import {SVGIcon} from '@homeaway/react-svg';

const TextAccordion = ({
    ACTION_LOCATION = 'text-component',
    EVENT_CATEGORY = 'interaction-click',
    baseCls = 'text-accordion',
    containerCls = 'text-container',
    dataWdio = 'text-container',
    headingStyle = 'span',
    panelIDPrefix = 'seo-description-type',
    useStructuredData = false,
    text = [],
}) => {
    const baseClsItem = `${baseCls}-item`;
    const [textState, setTextState] = useState(text);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const itemContentRefs = useRef([]);

    useEffect(() => {
        if (textState.length && !textState[0]?.hasOwnProperty('expanded')) {
            const newState = [...textState];
            textState[0].expanded = true;
            setTextState(newState);
        }

        const isCollapsing =
            textState[selectedIndex]?.isTransitioning &&
            !textState[selectedIndex]?.expanded;
        if (isCollapsing) {
            const newState = [...textState];
            // Force a reflow to ensure the initial height we previously
            // set is applied
            // eslint-disable-next-line
            document.documentElement.offsetLeft;
            // Transition the height to 0 by restoring its implicit value
            newState[selectedIndex].targetHeight = null;
            newState[selectedIndex].isTransitioning = false;

            setTextState(newState);
        }
    }, [textState, selectedIndex]);

    const fireItemToggledEdapEvent = (item) => {
        const fastpathEvent = {
            action_location: ACTION_LOCATION,
            event_category: EVENT_CATEGORY,
            event_action: item.expanded ? 'expand' : 'collapse',
        };

        if (item.panelID) {
            fastpathEvent.event_refinements = item.panelID;
        }

        pushEdapEvent('interaction.selected', fastpathEvent);
    };

    const expandContentAtIndex = (index) => {
        setSelectedIndex(index);
        const newState = [...textState];
        newState[index].expanded = !newState[index].expanded;
        newState[index].isTransitioning = true;
        // While expanding, this represents the height the element will transition
        // to. While collapsing, this is the height the element will
        // be initially set to before it transitions to 0.
        newState[index].targetHeight =
            itemContentRefs.current[index].scrollHeight;

        setTextState(newState);

        fireItemToggledEdapEvent(textState[index]);
    };

    const structuredData = (type) => {
        if (useStructuredData !== true) return {};

        switch (type) {
            case 'faq':
                return {
                    itemScope: true,
                    itemType: 'https://schema.org/FAQPage',
                };
            case 'question':
                return {
                    itemScope: true,
                    itemProp: 'mainEntity',
                    itemType: 'https://schema.org/Question',
                };
            case 'question-name':
                return {
                    itemProp: 'name',
                };
            case 'answer':
                return {
                    itemScope: true,
                    itemProp: 'acceptedAnswer',
                    itemType: 'https://schema.org/Answer',
                };
            case 'answer-text':
                return {
                    itemProp: 'text',
                };
            default:
                return {};
        }
    };

    const getHeadingTag = (style, index, titleTag) => {
        switch (style) {
            case 'heading':
                return index === 0 ? 'h2' : 'h3';
            case 'dynamic':
                return titleTag;
            default:
                return 'span';
        }
    };

    const textComponent = (
        <div className={'col-md-12'} {...structuredData('faq')}>
            {textState.map((item, index) => {
                const {
                    component = null,
                    content = null,
                    expanded,
                    isTransitioning,
                    targetHeight,
                    panelID,
                    title,
                    type,
                    titleTag,
                } = item;
                const panelIDLC = panelID
                    ? panelID.toLowerCase()
                    : `${baseClsItem}-${index}`;
                const contentCls = `${baseClsItem}__content ${baseCls}-type-${type} ${panelIDPrefix}-${panelIDLC}`;
                const contentID = `${baseClsItem}__${panelIDLC}`;
                const HeadingTag = getHeadingTag(headingStyle, index, titleTag);
                /* eslint-disable react/no-danger */
                return (
                    <div
                        className={`${baseClsItem} ${
                            expanded ? `${baseClsItem}__expanded` : ''
                        }`}
                        id={contentID}
                        {...structuredData('question')}
                        key={panelIDLC}
                    >
                        <HeadingTag
                            className={`${baseClsItem}__title`}
                            {...structuredData('question-name')}
                            role={'button'}
                            onClick={() => expandContentAtIndex(index)}
                        >
                            <SVGIcon
                                className={`${baseClsItem}__icon`}
                                markup={CHEVRON_DOWN__16}
                            />
                            <span className={`${baseClsItem}__title__text`}>
                                {title}
                            </span>
                        </HeadingTag>
                        <div
                            className={contentCls}
                            {...structuredData('answer')}
                            style={{
                                height: isTransitioning ? targetHeight : null,
                            }}
                            ref={(el) => (itemContentRefs.current[index] = el)}
                        >
                            <div
                                {...structuredData('answer-text')}
                                {...(component
                                    ? {children: component}
                                    : {
                                          dangerouslySetInnerHTML: {
                                              __html: content,
                                          },
                                      })}
                            />
                        </div>
                    </div>
                );
                /* eslint-enable react/no-danger */
            })}
        </div>
    );

    return (
        <div
            className={`container module ${containerCls} ${baseCls}`}
            data-wdio={dataWdio}
        >
            <EdapViewTracker
                eventData={{
                    action_location: ACTION_LOCATION,
                    position_fired: 'top',
                }}
                edapEvent={'content_module.presented'}
            />

            {!!textState.length && textComponent}

            <EdapViewTracker
                eventData={{
                    action_location: ACTION_LOCATION,
                    position_fired: 'bottom',
                }}
                edapEvent={'content_module.presented'}
            />
        </div>
    );
};

const textItem = PropTypes.shape({
    content: PropTypes.string,
    panelID: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    titleTag: PropTypes.string,
});

TextAccordion.propTypes = {
    ACTION_LOCATION: PropTypes.string,
    EVENT_CATEGORY: PropTypes.string,

    baseCls: PropTypes.string,
    containerCls: PropTypes.string,
    panelIDPrefix: PropTypes.string,
    headingStyle: PropTypes.oneOf(['heading', 'span', 'dynamic']),
    text: PropTypes.arrayOf(textItem),
    useStructuredData: PropTypes.bool,
};

export default TextAccordion;
